import isEmpty from 'lodash/isEmpty';
import lodashGet from 'lodash/get';
import lodashSet from 'lodash/set';

import {transitionInputParametersTaskValuePath} from './taskTabPolicy';
import {choiceTaskPrefix, parallelTaskPrefix} from './workflow';

const getCellValue = cell => cell?.getValue ? cell.getValue() : cell?.value;

const getCellName = cell => cell?.getName ? cell.getName() : getCellValue(cell)?.name;

const isTaskCell = cell => !cell?.source && !cell?.target && !cell?.edge;

const isPresent = value => value !== null && typeof value !== 'undefined';

const getChildren = (model, parent) => {
    if (!parent) {
        return [];
    }

    if (typeof model?.getChildCount === 'function' && typeof model?.getChildAt === 'function') {
        const children = [];
        const childCount = model.getChildCount(parent);
        for (let i = 0; i < childCount; i++) {
            children.push(model.getChildAt(parent, i));
        }
        return children;
    }

    if (typeof parent.getChildCount === 'function' && typeof parent.getChildAt === 'function') {
        const children = [];
        const childCount = parent.getChildCount();
        for (let i = 0; i < childCount; i++) {
            children.push(parent.getChildAt(i));
        }
        return children;
    }

    return parent.children || [];
};

const findCell = (model, predicate) => {
    const root = model?.getRoot?.();
    const seen = new Set();

    const visit = cell => {
        if (!cell || seen.has(cell)) {
            return null;
        }

        seen.add(cell);
        if (predicate(cell)) {
            return cell;
        }

        for (const child of getChildren(model, cell)) {
            const match = visit(child);
            if (match) {
                return match;
            }
        }

        return null;
    };

    return visit(root);
};

const findDescendantCell = (model, parent, predicate) => {
    const seen = new Set();

    const visit = cell => {
        if (!cell || seen.has(cell)) {
            return null;
        }

        seen.add(cell);
        if (predicate(cell)) {
            return cell;
        }

        for (const child of getChildren(model, cell)) {
            const match = visit(child);
            if (match) {
                return match;
            }
        }

        return null;
    };

    for (const child of getChildren(model, parent)) {
        const match = visit(child);
        if (match) {
            return match;
        }
    }

    return null;
};

const findCellById = (model, id) => id ? model?.getCell?.(id) || findCell(model, cell => cell?.id === id) : null;

const rootTaskScopePredicate = model => {
    const root = model?.getRoot?.();
    const mapLayerIds = new Set();
    findCell(model, cell => {
        const layerId = getCellValue(cell)?.itemProcessorId;
        if (layerId) {
            mapLayerIds.add(layerId);
        }
        return false;
    });

    return cell => {
        if (!isTaskCell(cell) || cell === root) {
            return false;
        }
        let parent = cell?.getParent ? cell.getParent() : cell?.parent;
        while (parent && parent !== root) {
            const value = getCellValue(parent);
            if (value?.type === 'Map' || value?.mapTaskId || mapLayerIds.has(parent.id)) {
                return false;
            }
            parent = parent.getParent ? parent.getParent() : parent.parent;
        }
        return true;
    };
};

const findTaskCellByName = (model, graph, taskName) => {
    const inRootScope = rootTaskScopePredicate(model);
    const mappedTaskId = graph?.taskIdMap?.get?.(taskName);
    const mappedTaskCell = mappedTaskId ? model?.getCell?.(mappedTaskId) : null;
    if (mappedTaskCell && inRootScope(mappedTaskCell)) {
        return mappedTaskCell;
    }

    return findCell(model, cell => inRootScope(cell) && getCellName(cell) === taskName);
};

const findTaskCellInTaskScope = (model, parentTaskCell, predicate) => {
    const itemProcessorLayer = findCellById(model, getCellValue(parentTaskCell)?.itemProcessorId);
    const itemProcessorMatch = findDescendantCell(model, itemProcessorLayer, cell => isTaskCell(cell) && predicate(cell));
    if (itemProcessorMatch) {
        return itemProcessorMatch;
    }

    return findDescendantCell(model, parentTaskCell, cell => isTaskCell(cell) && predicate(cell));
};

const taskContextName = taskContext => taskContext?.name;

const hasBranchContext = taskContext => Number.isInteger(taskContext?.branchIndex);

const taskContextWithLastName = (taskContexts, taskName) => taskContexts.map((taskContext, index) => index === taskContexts.length - 1
    ? {...taskContext, name: taskName}
    : taskContext);

const findTaskCellInContextScope = (model, parentTaskCell, taskContext, predicate) => {
    if (hasBranchContext(taskContext)) {
        return findTaskCellInParallelBranchScope(model, parentTaskCell, taskContext.branchIndex, predicate);
    }

    return findTaskCellInTaskScope(model, parentTaskCell, predicate);
};

const findTaskCellByContext = (model, graph, taskContexts) => {
    const taskName = taskContextName(taskContexts[taskContexts.length - 1]);
    if (!taskName) {
        return null;
    }

    let taskCell = findSyntheticSourceCell(model, graph, taskContextName(taskContexts[0]),
        'parallelTask', parallelTaskPrefix, [taskContexts[0]]);
    for (let i = 1; taskCell && i < taskContexts.length; i++) {
        const taskContext = taskContexts[i];
        const parentTaskCell = taskCell;
        taskCell = findTaskCellInContextScope(model, parentTaskCell, taskContext,
            cell => getCellName(cell) === taskContextName(taskContext)) ||
            findTaskCellInContextScope(model, parentTaskCell, taskContext,
                cell => getCellValue(cell)?.parallelTask?.name === taskContextName(taskContext));
        if (!taskCell) {
            return null;
        }
    }

    return taskCell;
};

const findTaskCellByScopedPredicate = (model, graph, taskContexts, predicate) => {
    const parentContexts = taskContexts.slice(0, -1);
    if (!isEmpty(parentContexts)) {
        const parentTaskCell = findTaskCellByContext(model, graph, parentContexts);
        const taskContext = taskContexts[taskContexts.length - 1];
        return findTaskCellInContextScope(model, parentTaskCell, taskContext, predicate);
    }

    const inRootScope = rootTaskScopePredicate(model);
    return findCell(model, cell => inRootScope(cell) && predicate(cell));
};

const findTaskCellByScopedName = (model, graph, taskContexts, taskName) => {
    const parentContexts = taskContexts.slice(0, -1);
    if (!isEmpty(parentContexts)) {
        const parentTaskCell = findTaskCellByContext(model, graph, parentContexts);
        const taskContext = taskContexts[taskContexts.length - 1];
        return findTaskCellInContextScope(model, parentTaskCell, taskContext, cell => getCellName(cell) === taskName);
    }

    return findTaskCellByName(model, graph, taskName);
};

const findSyntheticSourceCell = (model, graph, taskName, metadataField, syntheticPrefix, taskContexts = [{name: taskName}]) => {
    const directCell = findTaskCellByScopedName(model, graph, taskContexts, taskName);
    if (directCell) {
        return directCell;
    }

    const metadataCell = findTaskCellByScopedPredicate(model, graph, taskContexts, cell => getCellValue(cell)?.[metadataField]?.name === taskName);
    if (metadataCell) {
        return metadataCell;
    }

    if (!taskName?.startsWith(syntheticPrefix)) {
        return null;
    }

    const baseTaskName = taskName.substring(syntheticPrefix.length);
    return findTaskCellByScopedName(model, graph, taskContextWithLastName(taskContexts, baseTaskName), baseTaskName);
};

const isConditionalRoute = route => route?.isConditional ? route.isConditional() : route?.value?.routingType === 'Conditional';

const getOutgoingRoutes = (model, sourceCell) => sourceCell
    ? model?.getEdges?.(sourceCell, false, true, true) || []
    : [];

const getChoiceRoute = (model, sourceCell, choiceIndex) => getOutgoingRoutes(model, sourceCell)
    .filter(isConditionalRoute)[choiceIndex] || null;

const getDefaultRoute = (model, sourceCell) => getOutgoingRoutes(model, sourceCell)
    .find(route => !isConditionalRoute(route)) || null;

const getBranchRoute = (model, sourceCell, branchIndex) => getOutgoingRoutes(model, sourceCell)
    .filter(route => !isConditionalRoute(route))[branchIndex] || null;

const getRouteTargetCell = (model, route) => {
    if (!route?.target) {
        return null;
    }

    return typeof route.target === 'string' ? findCellById(model, route.target) : route.target;
};

const findReachableTaskCell = (model, startCell, stopCellId, predicate) => {
    const seen = new Set();

    const visit = cell => {
        const cellKey = cell?.id || cell;
        if (!cell || seen.has(cellKey) || (stopCellId && cell.id === stopCellId)) {
            return null;
        }

        seen.add(cellKey);
        if (isTaskCell(cell) && predicate(cell)) {
            return cell;
        }

        if (!isTaskCell(cell)) {
            for (const child of getChildren(model, cell)) {
                const childMatch = visit(child);
                if (childMatch) {
                    return childMatch;
                }
            }
        }

        for (const route of getOutgoingRoutes(model, cell)) {
            const routeMatch = visit(getRouteTargetCell(model, route));
            if (routeMatch) {
                return routeMatch;
            }
        }

        return null;
    };

    return visit(startCell);
};

const findTaskCellInParallelBranchScope = (model, sourceCell, branchIndex, predicate) => {
    const branchStartCell = getRouteTargetCell(model, getBranchRoute(model, sourceCell, branchIndex));
    return findReachableTaskCell(model, branchStartCell, getCellValue(sourceCell)?.joinTask, predicate);
};

const setNestedFieldError = (errors, id, field, message) => {
    if (!errors[id]) {
        errors[id] = {};
    }

    if (hasFieldPath(field)) {
        setFieldError(errors[id], field, message);
    } else {
        errors[id] = {
            ...errors[id],
            message
        };
    }
};

const isStructuredFieldError = value => value && typeof value === 'object' && !Array.isArray(value);

const hasFieldPath = field => Array.isArray(field) ? field.length > 0 : !!field;

const fieldPathParts = field => {
    if (Array.isArray(field)) {
        return field.flatMap(segment => {
            if (typeof segment === 'string' || typeof segment === 'number') {
                return [segment];
            }

            const parts = [];
            if (segment?.name) {
                parts.push(segment.name);
            }
            if (Number.isInteger(segment?.index)) {
                parts.push(segment.index);
            } else if (isPresent(segment?.key)) {
                parts.push(String(segment.key));
            }
            return parts;
        });
    }

    return hasFieldPath(field) ? [field] : [];
};

const setFieldError = (taskErrors, field, message) => {
    const path = ['fields', ...fieldPathParts(field)];

    for (let i = 1; i < path.length - 1; i++) {
        const parentPath = path.slice(0, i + 1);
        const value = lodashGet(taskErrors, parentPath);
        if (typeof value !== 'undefined' && !isStructuredFieldError(value) && !Array.isArray(value)) {
            lodashSet(taskErrors, parentPath, {message: value});
        }
    }

    const currentValue = lodashGet(taskErrors, path);
    if (isStructuredFieldError(currentValue)) {
        lodashSet(taskErrors, [...path, 'message'], message);
        return;
    }

    lodashSet(taskErrors, path, message);
};

const setRouteError = (routeErrors, route, message) => {
    if (!route) {
        return;
    }

    routeErrors[route.id] = {
        ...routeErrors[route.id],
        message
    };
};

const iterableContainerSegmentNames = new Set(['tasks', 'choices', 'branches', 'vectorStores', 'mcpServers']);

const hasIterableValue = segment => isPresent(segment?.key) || Number.isInteger(segment?.index);

const isIterableContainerSegment = segment => iterableContainerSegmentNames.has(segment?.name);

const applyIterableValue = (segment, key, index) => ({
    ...segment,
    key: isPresent(segment.key) ? segment.key : key,
    index: isPresent(segment.index) ? segment.index : index
});

const normalizeStructuredPath = fieldError => {
    const path = Array.isArray(fieldError?.path) ? fieldError.path : [];
    return path.reduce((segments, node) => {
        const name = node?.name || null;
        const key = isPresent(node?.key) ? String(node.key) : null;
        const index = Number.isInteger(node?.index) ? node.index : null;
        const hasNodeIterableValue = isPresent(key) || isPresent(index);

        if (name) {
            const lastSegment = segments[segments.length - 1];
            if ((name === '<map key>' || name === '<map value>') && isSegment(lastSegment, 'vectorStores')) {
                segments[segments.length - 1] = applyIterableValue(lastSegment, key, index);
                segments.push({name: name === '<map key>' ? 'name' : 'message', key: null, index: null});
                return segments;
            }
            if (hasNodeIterableValue && isIterableContainerSegment(lastSegment) && !hasIterableValue(lastSegment)) {
                segments[segments.length - 1] = applyIterableValue(lastSegment, key, index);
                segments.push({name, key: null, index: null});
                return segments;
            }

            segments.push({name, key, index});
        } else if (hasNodeIterableValue && segments.length > 0) {
            const lastSegment = segments[segments.length - 1];
            segments[segments.length - 1] = applyIterableValue(lastSegment, key, index);
        }

        return segments;
    }, []);
};

const segmentIndex = segment => Number.isInteger(segment?.index) ? segment.index : null;

const segmentKey = segment => isPresent(segment?.key) ? String(segment.key) : null;

const isSegment = (segment, name) => segment?.name === name;

const isIndexedSegment = (segment, name) => isSegment(segment, name) && Number.isInteger(segmentIndex(segment));

const isTasksKeySegment = segment => isSegment(segment, 'tasks') && isPresent(segmentKey(segment));

const taskContexts = (segments, taskSegmentIndex) => {
    let pendingBranchIndex = null;
    return segments.slice(0, taskSegmentIndex + 1).reduce((contexts, segment) => {
        if (isIndexedSegment(segment, 'branches')) {
            pendingBranchIndex = segmentIndex(segment);
            return contexts;
        }

        if (isTasksKeySegment(segment)) {
            contexts.push({
                name: segmentKey(segment),
                branchIndex: contexts.length > 0 ? pendingBranchIndex : null
            });
            pendingBranchIndex = null;
        }

        return contexts;
    }, []);
};

const isPropertyPath = (segments, name) => segments.length === 1
    && isSegment(segments[0], name)
    && !isPresent(segmentKey(segments[0]))
    && !Number.isInteger(segmentIndex(segments[0]));

const mapParallelBranchSegmentsError = ({fieldSegments, sourceCell, fieldError, model, taskErrors, routeErrors}) => {
    const branchSegment = fieldSegments[0];
    if (!isIndexedSegment(branchSegment, 'branches') || isSegment(fieldSegments[1], 'tasks') || !sourceCell) {
        return false;
    }

    const field = fieldSegments.slice(1);
    const route = getBranchRoute(model, sourceCell, segmentIndex(branchSegment));
    if (field.length === 0 || isPropertyPath(field, 'startTask')) {
        if (route) {
            setNestedFieldError(routeErrors, route.id, '', fieldError.message);
        } else {
            setNestedFieldError(taskErrors, sourceCell.id, fieldSegments, fieldError.message);
        }
    } else {
        setNestedFieldError(taskErrors, sourceCell.id, fieldSegments, fieldError.message);
    }
    return true;
};

const mapChoiceError = ({fieldSegments, taskName, contexts, fieldError, model, graph, routeErrors}) => {
    const choiceSegment = fieldSegments[0];
    if (!isIndexedSegment(choiceSegment, 'choices')) {
        return false;
    }

    const sourceCell = findSyntheticSourceCell(model, graph, taskName, 'decisionTask', choiceTaskPrefix, contexts);
    const route = getChoiceRoute(model, sourceCell, segmentIndex(choiceSegment));
    if (!route) {
        return false;
    }

    const field = fieldSegments.slice(1);
    if (isPropertyPath(field, 'nextTask')) {
        setRouteError(routeErrors, route, fieldError.message);
    } else {
        setNestedFieldError(routeErrors, route.id, field, fieldError.message);
    }
    return true;
};

const mapDefaultRouteError = ({fieldSegments, taskName, contexts, fieldError, model, graph, routeErrors}) => {
    if (!isPropertyPath(fieldSegments, 'defaultTask')) {
        return false;
    }

    const sourceCell = findSyntheticSourceCell(model, graph, taskName, 'decisionTask', choiceTaskPrefix, contexts);
    const route = getDefaultRoute(model, sourceCell);
    setRouteError(routeErrors, route, fieldError.message);
    return !!route;
};

const mapBranchError = ({fieldSegments, taskName, contexts, fieldError, model, graph, taskErrors, routeErrors}) => {
    const sourceCell = findSyntheticSourceCell(model, graph, taskName, 'parallelTask', parallelTaskPrefix, contexts);
    if (!sourceCell) {
        return false;
    }

    return mapParallelBranchSegmentsError({fieldSegments, sourceCell, fieldError, model, taskErrors, routeErrors});
};

const mapSyntheticTaskFieldError = ({fieldSegments, taskName, contexts, fieldError, model, graph, taskErrors}) => {
    for (const {metadataField, prefix} of [
        {metadataField: 'decisionTask', prefix: choiceTaskPrefix},
        {metadataField: 'parallelTask', prefix: parallelTaskPrefix},
    ]) {
        const sourceCell = findSyntheticSourceCell(model, graph, taskName, metadataField, prefix, contexts);
        const sourceTask = getCellValue(sourceCell);
        const isSyntheticTaskName = taskName === `${prefix}${sourceTask?.name}`;
        if (sourceTask?.[metadataField]?.name !== taskName
            && !(transitionInputParametersTaskValuePath(sourceCell, model) === metadataField && isSyntheticTaskName)) {
            continue;
        }

        setNestedFieldError(
            taskErrors,
            sourceCell.id,
            [metadataField, ...fieldSegments],
            fieldError.message,
        );
        return true;
    }

    return false;
};

const mapByStructuredPath = ({fieldError, model, graph, taskErrors, routeErrors}) => {
    const path = normalizeStructuredPath(fieldError);
    if (isEmpty(path)) {
        return false;
    }

    for (let i = path.length - 1; i >= 0; i--) {
        const taskSegment = path[i];
        if (!isTasksKeySegment(taskSegment)) {
            continue;
        }

        const taskName = segmentKey(taskSegment);
        const contexts = taskContexts(path, i);
        const fieldSegments = path.slice(i + 1);
        if (mapChoiceError({fieldSegments, taskName, contexts, fieldError, model, graph, routeErrors})) {
            return true;
        }

        if (mapDefaultRouteError({fieldSegments, taskName, contexts, fieldError, model, graph, routeErrors})) {
            return true;
        }

        if (mapBranchError({fieldSegments, taskName, contexts, fieldError, model, graph, taskErrors, routeErrors})) {
            return true;
        }

        if (mapSyntheticTaskFieldError({fieldSegments, taskName, contexts, fieldError, model, graph, taskErrors})) {
            return true;
        }

        const taskCell = findTaskCellByContext(model, graph, contexts);
        if (taskCell) {
            setNestedFieldError(taskErrors, taskCell.id, fieldSegments, fieldError.message);
            return true;
        }
    }

    return false;
};

export const mapServerFieldErrorsToGraphErrors = ({fieldErrors = {}, model, graph}) => {
    const routeErrors = {};
    const taskErrors = {};
    const workflowErrors = {};

    Object.values(fieldErrors).forEach(fieldError => {
        const path = normalizeStructuredPath(fieldError);
        const resourcesIndex = path.findIndex(segment => isSegment(segment, 'resources'));
        if (resourcesIndex > 0 && isSegment(path[resourcesIndex - 1], 'workflowDefinition')) {
            setFieldError(workflowErrors, path.slice(resourcesIndex), fieldError.message);
            return;
        }
        mapByStructuredPath({fieldError, model, graph, taskErrors, routeErrors});
    });

    return {
        workflowErrors,
        taskErrors,
        routeErrors
    };
};
